import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldX, Loader2, MapPin, User, Calendar, FileText, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Verify() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    const c = params.get("c");
    api
      .get(`/verify/${id}?c=${encodeURIComponent(c || "")}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch(() => setState({ loading: false, data: null, error: "This verification link is invalid or the record could not be found." }));
  }, [id, params]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-4">
          <img src={LOGO_URL} alt="RetroTrack" className="h-11 w-auto object-contain" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Record Verification</span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-10">
        {state.loading && (
          <div className="flex h-64 items-center justify-center" data-testid="verify-loading">
            <Loader2 className="h-7 w-7 animate-spin text-accent" />
          </div>
        )}

        {!state.loading && state.error && (
          <Card className="border-red-200 bg-red-50/50 p-8 text-center shadow-sm" data-testid="verify-error">
            <ShieldX className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Not verified</h1>
            <p className="mt-2 text-sm text-slate-600">{state.error}</p>
          </Card>
        )}

        {!state.loading && state.data && (
          <div className="space-y-6" data-testid="verify-success">
            <Card className="border-emerald-200 bg-emerald-50/50 p-8 text-center shadow-sm">
              <ShieldCheck className="mx-auto h-12 w-12 text-emerald-600" />
              <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Genuine record verified</h1>
              <p className="mt-2 text-sm text-slate-600">
                This report is a genuine {state.data.visit_type === "mid" ? "Mid" : "Post"} Coordination site visit held by CPH Retrofit.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <Badge className={`font-bold uppercase ${state.data.visit_type === "mid" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : "bg-accent/15 text-accent hover:bg-accent/15"}`}>
                  {state.data.visit_type === "mid" ? "Mid Coordination" : "Post Coordination"}
                </Badge>
                <Badge variant="outline" className={state.data.status === "completed" ? "border-emerald-500 text-emerald-700" : "border-slate-300 text-slate-500"}>
                  {state.data.status === "completed" ? "Completed" : "Draft"}
                </Badge>
              </div>
            </Card>

            <Card className="border-slate-200 p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-slate-900">Record Details</h2>
              <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                <Row icon={FileText} label="Reference" value={state.data.reference} />
                <Row icon={FileText} label="Client / Scheme" value={state.data.client_scheme} />
                <Row icon={MapPin} label="Property" value={`${state.data.job_address || "—"}${state.data.postcode ? ` · ${state.data.postcode}` : ""}`} />
                <Row icon={Calendar} label="Visit date" value={state.data.visit_date} />
                <Row icon={User} label="Completed by" value={`${state.data.completed_by || "—"}${state.data.completed_by_role ? ` · ${state.data.completed_by_role}` : ""}`} />
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {state.data.measures_installed} measure{state.data.measures_installed === 1 ? "" : "s"} installed
                </div>
                <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${state.data.open_faults > 0 ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                  <AlertTriangle className="h-4 w-4" /> {state.data.open_faults} open fault{state.data.open_faults === 1 ? "" : "s"}
                </div>
              </div>
              {state.data.measure_codes?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {state.data.measure_codes.map((c) => (
                    <span key={c} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{c}</span>
                  ))}
                </div>
              )}
            </Card>

            <p className="text-center text-xs text-slate-400">
              Verification provided by CPH Retrofit. Full evidence is available to authorised staff via the{" "}
              <Link to="/login" className="font-semibold text-accent hover:underline">coordinator portal</Link>.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
